import { Size } from "@hokuto/jam-core";
import { BACKGROUND_COLOR } from "../const.js";

export function getSizedCanvas(args: Size) {
  const { width, height } = args;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

export function getCanvasContext(args: {
  canvas?: HTMLCanvasElement;
  ctx?: CanvasRenderingContext2D;
  options?: CanvasRenderingContext2DSettings;
}) {
  const { canvas, ctx, options = { willReadFrequently: true } } = args;
  if (ctx) return ctx;
  if (!canvas) throw new Error("Expected canvas element");
  const context = canvas?.getContext("2d", options);
  if (!context) throw new Error("Invalid context");
  return context;
}

export function getImageData(args: {
  canvas?: HTMLCanvasElement;
  ctx?: CanvasRenderingContext2D;
  options?: CanvasRenderingContext2DSettings;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}) {
  const { canvas, ctx, options, x = 0, y = 0 } = args;
  const { width = canvas?.width, height = canvas?.height } = args;
  const context = getCanvasContext({ canvas, options, ctx });
  if (width === undefined || height === undefined) return;
  return context.getImageData(x, y, width, height);
}

export function resizeCanvas(
  args: Size & {
    canvas: HTMLCanvasElement;
    ctx?: CanvasRenderingContext2D;
    options?: CanvasRenderingContext2DSettings;
    bgColor?: string;
    dx?: number;
    dy?: number;
    dw?: number;
    dh?: number;
  }
) {
  const {
    canvas,
    ctx,
    options,
    width: destWidth,
    height: destHeight,
    bgColor = BACKGROUND_COLOR,
    dx = 0,
    dy = 0,
  } = args;

  const { dw = destWidth, dh = destHeight } = args;

  const context = getCanvasContext({ canvas, ctx, options });

  const imageData = getImageData({ canvas, ctx, options });

  const { width: srcWidth, height: srcHeight } = canvas;

  const width = destWidth * devicePixelRatio;
  const height = destHeight * devicePixelRatio;

  canvas.width = width;
  canvas.height = height;

  context.scale(devicePixelRatio, devicePixelRatio);

  canvas.style.width = `${destWidth}px`;
  canvas.style.height = `${destHeight}px`;

  if (!imageData) return;

  const previousCanvas = getSizedCanvas({ width: srcWidth, height: srcHeight });
  const previousContext = getCanvasContext({ canvas: previousCanvas });

  previousContext.putImageData(imageData, 0, 0);

  context.clearRect(0, 0, width, height);
  context.fillStyle = bgColor;
  context.fillRect(0, 0, width, height);
  context.drawImage(previousCanvas, dx, dy, dw, dh);
}

export function fromImageBitmap(img: ImageBitmap | HTMLImageElement) {
  const imgCanvas = getSizedCanvas(img);
  const context = getCanvasContext({ canvas: imgCanvas });
  context.drawImage(img, 0, 0);
  return imgCanvas;
}

export function getSafeRect(
  image: HTMLCanvasElement,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
  dx: number,
  dy: number,
  dw: number,
  dh: number
): [
  HTMLCanvasElement,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number
] {
  const { width, height } = image;

  if (sw < 0) {
    sx += sw;
    sw = Math.abs(sw);
  }
  if (sh < 0) {
    sy += sh;
    sh = Math.abs(sh);
  }
  if (dw < 0) {
    dx += dw;
    dw = Math.abs(dw);
  }
  if (dh < 0) {
    dy += dh;
    dh = Math.abs(dh);
  }
  const x1 = Math.max(sx, 0);
  const x2 = Math.min(sx + sw, width);
  const y1 = Math.max(sy, 0);
  const y2 = Math.min(sy + sh, height);
  const w_ratio = dw / sw;
  const h_ratio = dh / sh;

  return [
    image,
    x1,
    y1,
    x2 - x1,
    y2 - y1,
    sx < 0 ? dx - sx * w_ratio : dx,
    sy < 0 ? dy - sy * h_ratio : dy,
    (x2 - x1) * w_ratio,
    (y2 - y1) * h_ratio,
  ];
}
